<script lang="ts">import { buildBannerPath, getBannerFallbackPath } from "../utils";
import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { locale, t } from "@infinitro/content";
import { isMobile, loggedIn, ModalDialog, pushModal, toSiteUrl, toTitleCase } from "@infinitro/global";
/* ---------- props ---------- */
export let banner;
export let width = "100%";
export let height = "auto";
export let buttonContainerStyle = "";
export let buttonStyle = "";
export let showRightSideOfImage = false;
/* ---------- derived fields (single source: banner) ---------- */
let title = "";
let titleHighlight = "";
let secondPartTitle = "";
let description = "";
let button = false;
let buttonText = "";
let buttonTextLoggedIn = "";
let url = "";
let center = false;
let showTitle = true;
let onlyImage = true;
let noSignUp = false;
let desktopBannerSize = "1400x279";
let mobileBannerSize = "1080x405";
const BUTTON_VARIANTS = [
	"primary",
	"secondary",
	"tertiary",
	"accent",
	"transparent",
	"red"
];
$: ({title = "", titleHighlight = "", secondPartTitle = "", description = "", button = false, buttonText = "", buttonTextLoggedIn = "", url = "", center = false, showTitle = true, onlyImage = true, noSignUp = false, desktopBannerSize = "1400x279", mobileBannerSize = "1080x405"} = banner ?? {});
/* ---------- reactive state ---------- */
let selectedImage = "";
let selectedImageMb = "";
let fallbackImage = "";
let fallbackImageMb = "";
let bgEl;
$: useAlternative = $stateUi.bannerSwiper.bannerSwiperStyle === "alternative";
$: useUppercaseSectionTitles = $stateUi.layout.useUppercaseSectionTitles ?? true;
function formatBannerLine(key, text) {
	if (!key) return "";
	return useUppercaseSectionTitles ? text : toTitleCase(text);
}
$: bannerTitleDisplay = formatBannerLine(title, $t(title));
$: bannerTitleHighlightDisplay = formatBannerLine(titleHighlight, $t(titleHighlight));
$: bannerSecondPartDisplay = formatBannerLine(secondPartTitle, $t(secondPartTitle));
$: rawButtonVariant = Reflect.get($stateUi.bannerSwiper, "buttonVariant");
$: buttonVariant = BUTTON_VARIANTS.includes(rawButtonVariant) ? rawButtonVariant : "primary";
$: canRedirect = onlyImage && ($loggedIn || noSignUp);
/* ---------- helpers ---------- */
const clickSignUp = () => pushModal(ModalDialog.Auth, { tab: "signup" });
const onlyImageAction = (e) => {
	if (!canRedirect) {
		e.preventDefault();
		e.stopPropagation();
		clickSignUp();
	}
};
const setBg = (primary, fallback) => {
	const img = new Image();
	img.onload = () => bgEl.style.backgroundImage = `url(${primary})`;
	img.onerror = () => bgEl.style.backgroundImage = `url(${fallback})`;
	img.src = primary;
};
/* ---------- deterministic art ---------- */
$: {
	let variant = $loggedIn || noSignUp ? "default" : "signup";
	// locale folder if not English
	const loc = $locale !== "en" ? $locale : undefined;
	if (onlyImage) {
		selectedImage = buildBannerPath(banner, false, variant, loc);
		selectedImageMb = buildBannerPath(banner, true, variant, loc);
		fallbackImage = getBannerFallbackPath(banner, false, variant);
		fallbackImageMb = getBannerFallbackPath(banner, true, variant);
	}
}
$: setBg($isMobile ? selectedImageMb : selectedImage, $isMobile ? fallbackImageMb : fallbackImage);
</script>

<div
	class="banner"
	class:banner-alternative={useAlternative}
	style="width:{width};height:{height};"
>
	<div
		class="banner-content banner-image-ratio"
		class:banner-image-content-alternative={useAlternative}
		class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
		class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
		class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
		class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
	>
		{#if canRedirect}
			<a href={toSiteUrl(url, $loggedIn)} class="block">
				<div
					class="banner-image-ratio"
					bind:this={bgEl}
					class:background-image={!$isMobile}
					class:banner-image-mobile={$isMobile}
					class:show-right-side-of-image={showRightSideOfImage}
					class:banner-image-alternative={useAlternative}
					class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
					class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
					class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
					class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
				/>
			</a>
		{:else}
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<div
				class="banner-image-ratio"
				bind:this={bgEl}
				class:background-image={!$isMobile}
				class:banner-image-mobile={$isMobile}
				class:show-right-side-of-image={showRightSideOfImage}
				class:banner-image-alternative={useAlternative}
				class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
				class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
				class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
				class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
				on:click={onlyImage ? onlyImageAction : undefined}
			/>
		{/if}

		{#if !onlyImage}
			<div class="text-content {center && 'text-content-center'}">
				<div class="banner-title">
					{#if showTitle}
						<h3 class="title" class:title-uppercase={useUppercaseSectionTitles}>
							{bannerTitleDisplay}
						</h3>
					{/if}
					<h3
						class="title highlight"
						class:title-uppercase={useUppercaseSectionTitles}
						class:not-show-title={$t(title).trim() === '\u200B'}
					>
						{bannerTitleHighlightDisplay}
					</h3>
					<h3 class="title" class:title-uppercase={useUppercaseSectionTitles}>
						{bannerSecondPartDisplay}
					</h3>
				</div>

				{#if description}
					<p class="description">{$t(description)}</p>
				{/if}

				{#if button}
					<div class="button-container" style={buttonContainerStyle}>
						{#if $loggedIn}
							<a href={`/player${url}`}>
								<Button
									text={$t(buttonTextLoggedIn)}
									variant={buttonVariant}
									styleCustom={buttonStyle}
									minWidth
								/>
							</a>
						{:else}
							<Button
								text={$t(buttonText)}
								variant={buttonVariant}
								styleCustom={buttonStyle}
								minWidth
								onClick={clickSignUp}
							/>
						{/if}
					</div>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style>
	.banner {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 16px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}

	.banner-content {
		position: relative;
		min-height: 160px;
		width: 100%;
}

	@media (min-width: 1024px) {

		.banner-content {
				min-height: 248px;
		}
}

	.background-image,
	.banner-image-mobile {
		position: fixed;
		top: 0px;
		left: 0px;
		height: 160px;
		width: 100%;
		border-radius: 16px;
		background-size: cover;
		-o-object-fit: cover;
		   object-fit: cover;
}

	@media (min-width: 1024px) {

		.background-image,
	.banner-image-mobile {
				height: 248px;
		}
}

	.background-image,
	.banner-image-mobile {
		background-position: right center;
	}

	.show-right-side-of-image {
		background-position: right bottom;
		background-repeat: no-repeat;
}

	@media (min-width: 768px) {

		.show-right-side-of-image {
				background-position: right;
				background-repeat: no-repeat;
		}
}

	.text-content {
		width: 100%;
		padding: 0.75rem;
}

	@media (min-width: 1024px) {

		.text-content {
				padding: 1.5rem;
		}
}

	.banner-title {
		display: flex;
		gap: 0.5rem;
}

	.title {
		margin-bottom: 0px;
		text-align: start;
		font-size: 16px;
		font-weight: 800;
}

	@media (min-width: 1024px) {

		.title {
				font-size: 40px;
				line-height: 40px;
		}
}

	.title-uppercase {
		text-transform: uppercase;
}

	.highlight {
		color: var(--color-primary-500);
	}

	.description {
		margin-top: 0px;
		margin-bottom: 0.25rem;
		text-align: start;
		font-size: 10px;
		font-weight: 100;
}

	@media (min-width: 1024px) {

		.description {
				font-size: 18px;
		}
}

	.description {
		white-space: pre-wrap;
	}

	.text-content {
		position: absolute;
		top: 0px;
		left: 0px;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		gap: 0.5rem;
}

	.text-content-center {
		align-items: center;
}

	.not-show-title {
		margin-left: -8px;
}

	.banner-alternative {
		border-radius: 0px;
}

	.banner-image-content-alternative,
	.banner-image-alternative {
		height: auto;
		max-height: 100%;
		min-height: 0px;
		width: 100%;
		cursor: pointer;
		border-radius: 0px;
}
	.banner-image-ratio_27_10 {
		aspect-ratio: 2.7/1;
}
	.banner-image-ratio_1_1 {
		aspect-ratio: 1/1;
}
	.banner-image-ratio_5_1 {
		aspect-ratio: 5/1;
}
	.banner-image-ratio_4_1 {
		aspect-ratio: 4/1;
}
</style>
